"use client"

import {
  ArchiveBox,
  ArrowRightOnRectangle,
  House,
  MapPin,
  User,
} from "@medusajs/icons"
import { useParams, usePathname } from "next/navigation"

import { signout } from "@lib/data/customer"
import { useFrontendPreferences } from "@lib/frontend-preferences"
import { HttpTypes } from "@medusajs/types"
import LocalizedClientLink from "@modules/common/components/localized-client-link"

const AccountNav = ({
  customer,
}: {
  customer: HttpTypes.StoreCustomer | null
}) => {
  const { t } = useFrontendPreferences()
  const route = usePathname()
  const { countryCode } = useParams() as { countryCode: string }

  const handleLogout = async () => {
    await signout(countryCode)
  }

  const current = route?.split(countryCode)[1] || ""
  const initials =
    `${customer?.first_name?.[0] ?? ""}${customer?.last_name?.[0] ?? ""}` ||
    customer?.email?.[0]?.toUpperCase() ||
    "S"

  const items = [
    { href: "/account", label: t.account.overview, icon: <House />, id: "overview-link" },
    { href: "/account/profile", label: t.account.profile, icon: <User />, id: "profile-link" },
    { href: "/account/addresses", label: t.account.addresses, icon: <MapPin />, id: "addresses-link" },
    { href: "/account/orders", label: t.account.orders, icon: <ArchiveBox />, id: "orders-link" },
  ]

  return (
    <aside
      className="sahil-acc-card min-w-0 w-full p-4 small:p-5 small:sticky small:top-28"
      data-testid="account-nav"
    >
      <div className="flex items-center gap-x-4 p-1 small:p-2 pb-4 small:pb-5 mb-3 border-b border-[var(--acc-line)]">
        <div className="sahil-acc-avatar" aria-hidden>
          {initials}
        </div>
        <div className="min-w-0">
          <p className="font-semibold truncate">
            {[customer?.first_name, customer?.last_name].filter(Boolean).join(" ") ||
              t.account.dashboard}
          </p>
          <p className="text-small-regular sahil-acc-muted truncate" dir="ltr">
            {customer?.email}
          </p>
        </div>
      </div>

      <nav
        aria-label={t.account.dashboard}
        className="grid grid-cols-2 gap-2 small:flex small:flex-col small:gap-1"
      >
        {items.map((item) => (
          <LocalizedClientLink
            key={item.href}
            href={item.href}
            className="sahil-acc-nav-link"
            data-active={current === item.href}
            aria-current={current === item.href ? "page" : undefined}
            data-testid={item.id}
          >
            {item.icon}
            <span>{item.label}</span>
          </LocalizedClientLink>
        ))}
        <button
          type="button"
          onClick={handleLogout}
          className="sahil-acc-nav-link small:mt-3 small:border-t small:border-[var(--acc-line)] small:rounded-t-none small:pt-4 text-start"
          data-testid="logout-button"
        >
          <ArrowRightOnRectangle />
          <span>{t.account.logOut}</span>
        </button>
      </nav>
    </aside>
  )
}

export default AccountNav
